
import { Injectable } from '@angular/core';
import { BIOMETRIC_ERRORS, FingerprintAIO, FingerprintOptions } from '@awesome-cordova-plugins/fingerprint-aio/ngx';
import { PlatformService } from './platform.service';
import { AsyncSubject, combineLatest, firstValueFrom } from 'rxjs';
import { TranslateUtilityService } from './translation-utility.service';
import { Platform } from '@ionic/angular';
import { SecureStorageService, SecureStorageType } from './secure-storage.service';
import { BiometricToggleBean } from 'src/app/business/biometrics/biometrics-bean';
import { logger } from 'src/app/common/services/logging/logging.service';
import { LocalStorageService, LocalStorageType } from './local-storage.service';
import { StringUtilityService } from './string-utility.service';
import { MEAPIService } from 'src/app/common/services/api/me-api.service';
import { MEBasicAPIResponse, MEBasicAPIResponseHandler, MEDoNothingBasicResponseHandler, ResponseCode } from 'src/app/common/services/api/me-api-response';
import { MfaService } from 'src/app/common/components/mfa/mfa.service';
import { MFAResult, MFAScope } from 'src/app/common/components/mfa/mfa-bean';
import { UserService } from 'src/app/common/services/user/user.service';

@Injectable({
    providedIn: 'root'
})
export class BiometricService {
    private static readonly API_BIOMETRIC_CHANGE = MEAPIService.BASE_URL + '/customer/biometrics';
    private static readonly API_BIOMETRIC_SUSPEND = MEAPIService.BASE_URL + '/customer/biometrics/suspend';
    /** Sentinel stored by the plugin. Invalidated when a fingerprint or face is added or removed. */
    private static readonly ENROLLMENT_SECRET = 'biometric-set';

    private translateMap = new Map<BiometricType, string>([]);
    private isPlatformSupport!: BiometricStatus;
    /** Set for the login attempt that just ran the enrollment check. */
    private enrollmentSheetShown = false;
    private enrollmentSheetVerified = false;
    private enrollmentSheetLockedOut = false;

    constructor(
        private platform: Platform,
        private fp: FingerprintAIO,
        private translate: TranslateUtilityService,
        private srvSecureStorage: SecureStorageService,
        private srvLocalStorage: LocalStorageService,
        private srvAPI: MEAPIService,
        private srvMFA: MfaService,
        srvUser: UserService,
    ) {
        // Keep the enrollment-sheet result through MFA and every other next condition.
        // Clear it only once that chain finishes and the user is fully logged in.
        srvUser.isLoggedIn$.subscribe((loggedIn) => {
            if (loggedIn) {
                this.resetEnrollmentSheetState();
            }
        });
        this.translateMap.set(BiometricType.FINGER, 'BIOMETRICS.FINGERPRINT');
        this.translateMap.set(BiometricType.FACE, 'BIOMETRICS.FACE_ID');
        this.translateMap.set(BiometricType.BIOMETRIC, 'BIOMETRICS.BIOMETRIC');
    }

    public getPlatformBiometricStatus(): AsyncSubject<BiometricStatus> {
        let subject = new AsyncSubject<BiometricStatus>();
        if (!PlatformService.isMobile()) {
            let bean = BiometricNotSupport;
            this.isPlatformSupport = bean;
            subject.next(bean);
            subject.complete();
        }
        else {
            this.platform.ready().then(() => {

                combineLatest([
                    this.fp.isAvailable(),
                ]).subscribe({
                    next: ([isAvailable]) => {
                        if (isAvailable) {
                            let bean: BiometricStatus = {
                                isPlatformSupport: true,
                                biometricType: isAvailable as BiometricType
                            };

                            this.isPlatformSupport = bean;

                            subject.next(bean);
                            subject.complete();
                        };
                    },
                    error: (err) => {
                        
                        let bean = BiometricNotSupport;
                        this.isPlatformSupport = bean;
                        subject.next(bean);
                        subject.complete();
                    }
                });
            });
        }
        return subject;
    }


    public isProfileEnableBiometric(): AsyncSubject<boolean> {
        let subject = new AsyncSubject<boolean>();
        combineLatest([
            this.srvSecureStorage.get(SecureStorageType.CREDENTIAL)
        ]).subscribe(([credential]) => {

            if (credential.length === 0 || !StringUtilityService.isValidJSON(credential)) {
                subject.next(false);
                subject.complete();
                return;
            }

            // Ensure Same User
            let json = JSON.parse(credential);            ;
            let userId = this.srvLocalStorage.get(LocalStorageType.REMEMBER_ME);
            let isProfileMatch = (json.username === userId);
            if (!isProfileMatch) {
                subject.next(false);
                subject.complete();
                return;
            }

            // Check if Biometric is enabled
            let isProfileEnable = json.isEnableBiometric;
            subject.next(isProfileEnable);
            subject.complete();
        });
        return subject;
    }

    public performBiometricChallenge(bean: BiometricChallenge): AsyncSubject<BiometricResult> {
        let subject = new AsyncSubject<BiometricResult>();

        this.performBiometric(bean).subscribe((status: BiometricResultStatus) => {
            if (status === BiometricResultStatus.SUCCESS) {
                this.srvSecureStorage.get(SecureStorageType.CREDENTIAL).subscribe((credential) => {
                    let tmp: BiometricCredential = JSON.parse(credential);
                    let bean: BiometricResult = {
                        status: BiometricResultStatus.SUCCESS,
                        username: tmp.username,
                        password: tmp.password
                    };
                    subject.next(bean);
                    subject.complete();
                });    
            }
            else {
                subject.next({ status });
                subject.complete();
            }
        });

        return subject;
    }

    public performBiometric(bean: BiometricChallenge): AsyncSubject<BiometricResultStatus> {
        let subject = new AsyncSubject<BiometricResultStatus>();

        if (this.enrollmentSheetShown) {
            if (this.enrollmentSheetVerified) {
                subject.next(BiometricResultStatus.SUCCESS);
                subject.complete();
            }
            else {
                subject.next(BiometricResultStatus.FAILED);
                subject.complete();
            }
            return subject;
        }

        if (this.enrollmentSheetLockedOut) {
            subject.next(BiometricResultStatus.LOCKED_OUT);
            subject.complete();
            return subject;
        }

        let fingerprintOptions: FingerprintOptions = {
            description: bean.description,
            disableBackup: true,
        }

        this.fp.show(fingerprintOptions).then((result) => {
                subject.next(BiometricResultStatus.SUCCESS);
                subject.complete();
        }).catch((err) => {
            logger.error(err);
            subject.next(this.mapPluginError(err));
            subject.complete();

        });
        return subject;
    }

    /** True when the enrollment check opened a system sheet for this login attempt. */
    public wasEnrollmentSheetShown(): boolean {        
        return this.enrollmentSheetShown;
    }

    /** True when the user completed the enrollment sheet. That verification replaces the sign-in sheet. */
    public wasEnrollmentSheetVerified(): boolean {
        return this.enrollmentSheetVerified;
    }

    public wasEnrollmentSheetLockedOut(): boolean {
        return this.enrollmentSheetLockedOut;
    }

    /**
     * True when a fingerprint or face was added or removed since the last bound secret.
     * The first call binds a secret with invalidateOnEnrollment and reports no change.
     * Loading a still-valid secret shows the OS prompt. An invalidated secret fails
     * with BIOMETRIC_SECRET_NOT_FOUND and does not prompt.
     */
    public async hasEnrollmentChanged(): Promise<boolean> {
        this.resetEnrollmentSheetState();
        if (!PlatformService.isMobile()) {
            return false;
        }

        const registered = await this.isEnrollmentSecretRegistered();
        let available = false;
        try {
            await this.fp.isAvailable();
            available = true;
        } catch (err) {
            logger.error(err);
        }

        if (!available) {
            return registered;
        }

        if (!registered) {
            const bound = await this.bindEnrollmentSecret();
            // Android registerBiometricSecret opens a sheet. iOS saves the secret without one.
            if (PlatformService.isAndroid()) {
                this.enrollmentSheetShown = true;
                this.enrollmentSheetVerified = bound;
            }
            return false;
        }

        try {
            await this.fp.loadBiometricSecret({
                description: this.translate.instant('BIOMETRICS.BIOMETRIC'),
                disableBackup: true,
            });
            this.enrollmentSheetShown = true;
            this.enrollmentSheetVerified = true;
            return false;
        } catch (err) {
            const code = this.getPluginErrorCode(err);
            if (code === BIOMETRIC_ERRORS.BIOMETRIC_SECRET_NOT_FOUND
                || code === BIOMETRIC_ERRORS.BIOMETRIC_NOT_ENROLLED) {
                return true;
            }
            if (code === BIOMETRIC_ERRORS.BIOMETRIC_DISMISSED
                || code === BIOMETRIC_ERRORS.BIOMETRIC_PIN_OR_PATTERN_DISMISSED
                || code === BIOMETRIC_ERRORS.BIOMETRIC_AUTHENTICATION_FAILED) {
                this.enrollmentSheetShown = true;
                return false;
            }
            if (code === BIOMETRIC_ERRORS.BIOMETRIC_LOCKED_OUT
                || code === BIOMETRIC_ERRORS.BIOMETRIC_LOCKED_OUT_PERMANENT) {
                this.enrollmentSheetShown = true;
                this.enrollmentSheetLockedOut = true;
                return false;
            }
            logger.error(err);
            return false;
        }
    }

    private resetEnrollmentSheetState(): void {
        this.enrollmentSheetShown = false;
        this.enrollmentSheetVerified = false;
        this.enrollmentSheetLockedOut = false;
    }

    /**
     * Bind a secret to the current biometric set so the next login can see a later change.
     * Call after a login that already reported a change.
     */
    public async bindEnrollmentSecret(): Promise<boolean> {
        if (!PlatformService.isMobile()) {
            return false;
        }
        try {
            await this.fp.registerBiometricSecret({
                description: this.translate.instant('BIOMETRICS.BIOMETRIC'),
                secret: BiometricService.ENROLLMENT_SECRET,
                invalidateOnEnrollment: true,
                disableBackup: true,
            });
            return await firstValueFrom(this.srvSecureStorage.set(SecureStorageType.BIOMETRIC_ENROLLMENT, '1'));
        } catch (err) {
            logger.error(err);
            return false;
        }
    }

    private async isEnrollmentSecretRegistered(): Promise<boolean> {
        const value = await firstValueFrom(this.srvSecureStorage.get(SecureStorageType.BIOMETRIC_ENROLLMENT));
        return value === '1';
    }

    public suspendBiometrics(userId: string): AsyncSubject<boolean> {
        return this.srvAPI.BasicPost(BiometricService.API_BIOMETRIC_SUSPEND,{ userId },[new MEDoNothingBasicResponseHandler(ResponseCode.SUCCESS)]);
    }

    public getBiometricName(biometricType: BiometricType): string {
        let key = this.translateMap.get(biometricType) || '';
        return this.translate.instant(key);
    }

    public storeBiometricCredential(credential: BiometricCredential): AsyncSubject<boolean> {
        let subject = new AsyncSubject<boolean>();
        this.srvSecureStorage.set(SecureStorageType.CREDENTIAL, JSON.stringify(credential)).subscribe((success) => {
            subject.next(success);
            subject.complete();
        });
        return subject;
    }

    /** Flips isEnableBiometric on the credential already stored at login; does not call the biometrics change API. */
    public enableBiometricsOnDevice(): AsyncSubject<boolean> {
        let subject = new AsyncSubject<boolean>();
        this.srvSecureStorage.get(SecureStorageType.CREDENTIAL).subscribe((raw) => {
            try {
                if (!raw || !StringUtilityService.isValidJSON(raw)) {
                    subject.next(false);
                    subject.complete();
                    return;
                }
                let credential: BiometricCredential = JSON.parse(raw);
                credential.isEnableBiometric = true;
                this.storeBiometricCredential(credential).subscribe((success) => {
                    subject.next(success);
                    subject.complete();
                });
            } catch {
                subject.next(false);
                subject.complete();
            }
        });
        return subject;
    }

    public updateBiometric(bean: BiometricToggleBean): AsyncSubject<boolean> {
        let subject = new AsyncSubject<boolean>();
        let apiBean = {
            "activated": bean.isEnableBiometrics
        };
        this.srvAPI.Post(BiometricService.API_BIOMETRIC_CHANGE, 
            apiBean,
            [
                new BiometricSuccessHandler(this, subject, bean.isEnableBiometrics),
                new BiometricsMFAHandler(this, subject, bean.isEnableBiometrics)
            ])
        return subject;
    }

    /**
     * Disable biometrics on the server and clear local biometric / App Authenticator storage.
     * Same endpoint as {@link updateBiometric}(false), but never starts nested MFA
     * (used mid-login when MFA_AUTH_APP / MFA_AUTH_APP_SELF is already in progress).
     */
    public disableBiometricsForOrphanedAppAuth(): AsyncSubject<boolean> {
        let subject = new AsyncSubject<boolean>();
        const finish = (success: boolean) => {
            if (subject.closed) {
                return;
            }
            this.clearBiometricAppAuth();
            subject.next(success);
            subject.complete();
        };

        this.srvAPI.Post(BiometricService.API_BIOMETRIC_CHANGE,
            { activated: false },
            [
                new BiometricOrphanDisableHandler(ResponseCode.SUCCESS, finish),
                new BiometricOrphanDisableHandler(ResponseCode.MFA_VALIDATION, finish),
                new BiometricOrphanDisableHandler(ResponseCode.ALL_CODE_HANDLER, finish),
            ]).subscribe({
                next: (ok) => finish(!!ok),
                error: (err) => {
                    logger.error(err);
                    finish(false);
                },
            });
        return subject;
    }

    /**
     * After the API/MFA accepts a biometric change, persist the matching local state.
     * Enable/disable only flips isEnableBiometric; username and password from login are kept.
     */
    public applyBiometricChangeLocally(isEnableBiometrics: boolean, ret: AsyncSubject<boolean>): void {
        if (isEnableBiometrics) {
            this.enableBiometricsOnDevice().subscribe((success) => {
                if (!success) {
                    logger.debug('Failed to persist enabled biometric flag to secure storage');
                }
                ret.next(true);
                ret.complete();
            });
        }
        else {
            this.disableBiometricsOnDevice().subscribe(() => {
                ret.next(true);
                ret.complete();
            });
        }
    }

    /** Clears App Authenticator and sets isEnableBiometric false without dropping the login password. */
    private disableBiometricsOnDevice(): AsyncSubject<boolean> {
        let subject = new AsyncSubject<boolean>();
        this.srvSecureStorage.get(SecureStorageType.CREDENTIAL).subscribe((credentialJson) => {
            if (!credentialJson || !StringUtilityService.isValidJSON(credentialJson)) {
                subject.next(true);
                subject.complete();
                return;
            }

            let credential: BiometricCredential = JSON.parse(credentialJson);
            credential.isEnableBiometric = false;
            this.storeBiometricCredential(credential).subscribe((success) => {
                subject.next(success);
                subject.complete();
            });
        });
        return subject;
    }

    public isEnableBiometric(): AsyncSubject<boolean> {
        let subject = new AsyncSubject<boolean>();
        this.srvSecureStorage.get(SecureStorageType.CREDENTIAL).subscribe((credential) => {
            let isProfileEnable = (credential !== "") && JSON.parse(credential).isEnableBiometric;
            subject.next(isProfileEnable);
            subject.complete();
        });
        return subject;
    }

    public setupBiometricCredential(credential: BiometricCredential) {
        this.srvSecureStorage.set(SecureStorageType.CREDENTIAL, JSON.stringify(credential));
    }

    public removeBiometricCredential() {
        this.srvSecureStorage.remove(SecureStorageType.CREDENTIAL);
    }

    public getCredential(): AsyncSubject<BiometricCredential> {
        let subject = new AsyncSubject<BiometricCredential>();
        this.srvSecureStorage.get(SecureStorageType.CREDENTIAL).subscribe((credential) => {
            if (credential === "") {
                subject.next({username: "",
                    password: "",
                    isEnableBiometric: false,
                    biometricType: BiometricType.BIOMETRIC
                });
            } else {
                subject.next(JSON.parse(credential));
            }
            subject.complete();
        });
        return subject;
    }

    // This is a shortcut to avoid async if the platform is enable biometric
    // Value was assigned during isPlatformEnableBiometric during Platform Init
    public isPlatformSupportBiometricLogin(): BiometricStatus {
        return this.isPlatformSupport;
    }

    public showMfa(ret: AsyncSubject<boolean>, isEnableBiometrics: boolean) {
        this.srvMFA.preauth(MFAScope.BIOMETRIC_CHANGE, { useAppAuth: false }).subscribe((res: MFAResult) => {
              switch (res) {
                case MFAResult.VERIFIED:
                    this.applyBiometricChangeLocally(isEnableBiometrics, ret);
                  break;
                case MFAResult.TOO_MANY:
                case MFAResult.UNVERIFIED:
                case MFAResult.EXPIRED:
                  ret.next(false);
                  ret.complete();
                  break;
              }
        });
    }

    public clearBiometricAppAuth(): void {
        this.srvSecureStorage.remove(SecureStorageType.APP_AUTHENTICATOR_PRIVATE_KEY);
    }

    private mapPluginError(err: any): BiometricResultStatus {
        const code = this.getPluginErrorCode(err);
        switch (code) {
            case BIOMETRIC_ERRORS.BIOMETRIC_DISMISSED:
            case BIOMETRIC_ERRORS.BIOMETRIC_PIN_OR_PATTERN_DISMISSED:
                return BiometricResultStatus.CANCEL;
            case BIOMETRIC_ERRORS.BIOMETRIC_LOCKED_OUT:
                return BiometricResultStatus.LOCKED_OUT;
            case BIOMETRIC_ERRORS.BIOMETRIC_LOCKED_OUT_PERMANENT:
                return BiometricResultStatus.LOCKED_OUT_PERMANENT;
            default:
                return BiometricResultStatus.FAILED;
        }
    }

    private getPluginErrorCode(err: any): number {
        if (typeof err === 'number') {
            return err;
        }
        const raw = err?.code ?? err?.error;
        if (typeof raw === 'number') {
            return raw;
        }
        if (typeof raw === 'string') {
            const parsed = parseInt(raw, 10);
            return Number.isNaN(parsed) ? BIOMETRIC_ERRORS.BIOMETRIC_UNKNOWN_ERROR : parsed;
        }
        return BIOMETRIC_ERRORS.BIOMETRIC_UNKNOWN_ERROR;
    }



}

export interface BiometricStatus {
    isPlatformSupport: boolean;
    biometricType?: BiometricType;
}

export enum BiometricType {
    FINGER = 'finger',
    FACE = 'face',
    BIOMETRIC = 'biometric'
}

export interface BiometricResult {
    status: BiometricResultStatus;
    username?: string;
    password?: string;
}

export enum BiometricResultStatus {
    SUCCESS = 'success',
    CANCEL = 'cancel',
    FAILED = 'failed',
    LOCKED_OUT = 'locked_out',
    LOCKED_OUT_PERMANENT = 'locked_out_permanent'
}

export interface BiometricCredential {
    username: string;
    password: string;
    isEnableBiometric: boolean;
    biometricType: BiometricType;
}

export interface BiometricChallenge{
    biometricType: BiometricType;
    description: string;
}


export const BiometricNotSupport: BiometricStatus = { isPlatformSupport: false };
export const BiometricCancel: BiometricResult = { status: BiometricResultStatus.CANCEL };
export const BiometricFailed: BiometricResult = { status: BiometricResultStatus.FAILED };
export const BiometricLockedOut: BiometricResult = { status: BiometricResultStatus.LOCKED_OUT };
export const BiometricLockedOutPermanent: BiometricResult = { status: BiometricResultStatus.LOCKED_OUT_PERMANENT };

class BiometricSuccessHandler implements MEBasicAPIResponseHandler {
    public readonly code: ResponseCode = ResponseCode.SUCCESS;

    constructor(
        private readonly srvBiometric: BiometricService,
        private readonly ret: AsyncSubject<boolean>,
        private readonly isEnableBiometrics: boolean
    ) {}

    handle(_response: MEBasicAPIResponse) : boolean {
        this.srvBiometric.applyBiometricChangeLocally(this.isEnableBiometrics, this.ret);
        return true;
    }
}

class BiometricsMFAHandler implements MEBasicAPIResponseHandler {
  public readonly code: ResponseCode = ResponseCode.MFA_VALIDATION;

  constructor(
    private readonly srvBiometric: BiometricService,
    private readonly ret: AsyncSubject<boolean>,
    private readonly isEnableBiometrics: boolean
  ) {}

  handle(_response: MEBasicAPIResponse): boolean {
    this.srvBiometric.showMfa(this.ret, this.isEnableBiometrics);
    return false;
  }
}

/** Completes orphan-disable without nested MFA; local clear is done by the caller finish callback. */
class BiometricOrphanDisableHandler implements MEBasicAPIResponseHandler {
  constructor(
    public readonly code: ResponseCode,
    private readonly finish: (success: boolean) => void,
  ) {}

  handle(_response: MEBasicAPIResponse): boolean {
    this.finish(true);
    return true;
  }
}